import { useMemo } from 'react'

import { useOrg } from '@/admin/hooks/use-org'

/**
 * Whether this session may choose the partner a record is attributed to, and
 * from which set. One rule, shared by every partner/dealer picker, so the
 * registration wizard's call sites cannot drift apart.
 *
 * The question is not "is this an internal user?" but "does this session have a
 * real choice to make?":
 *
 *  - Internal session - yes, from the org's partner book. Callers keep their
 *    own additional gate (e.g. the Dealer field also needs the org to HAVE a
 *    dealer network); this hook does not second-guess it.
 *  - Partner session  - only when they have sub-locations, and only from their
 *    own partnership plus those. A single-site partner has nothing to choose
 *    between, so the picker stays hidden exactly as it was before.
 *
 * A partner session never makes the field required: the create endpoint
 * already attributes a blank selection to the caller's own partnership, so
 * leaving it alone is both valid and the previous behaviour.
 */
export interface PartnerPickerScope {
  /** This session has some choice to offer. Callers may still gate further. */
  visible: boolean
  /** Partner choosing among its own locations, rather than the whole book. */
  ownLocationsOnly: boolean
  /** What a blank selection resolves to; null when there is no implicit default. */
  defaultsToName: string | null
  /** Is a selection mandatory? Never for a partner - blank resolves to self. */
  required: boolean
}

export function usePartnerPickerScope(): PartnerPickerScope {
  const {
    isPartnerAccess,
    activePartnerLocationCount,
    activePartnerName,
    tenantSettings,
  } = useOrg()
  const requireOnRegistration =
    tenantSettings?.require_partner_on_registration ?? false

  return useMemo<PartnerPickerScope>(
    () => ({
      visible: !isPartnerAccess || activePartnerLocationCount > 0,
      ownLocationsOnly: isPartnerAccess,
      defaultsToName: isPartnerAccess ? activePartnerName : null,
      required: !isPartnerAccess && requireOnRegistration,
    }),
    [
      isPartnerAccess,
      activePartnerLocationCount,
      activePartnerName,
      requireOnRegistration,
    ],
  )
}
